// patients-view.js — CLIENT-SIDE shaping, raw FHIR -> the buildPatientsView
// row DTO (server/opd/view-adapter.mjs:2647), computed from a Patient search
// with _revinclude=Encounter:subject.
//
// NAMES-TO-ALL is free under RLS: the current opd-doctor-rowlevel AccessPolicy
// gives Patient no criteria (demographics open to everyone — REDESIGN_ONLINE_PRIMARY.md
// P0), so an unscoped Patient search already returns every patient; only the
// _revinclude'd Encounters are RLS-filtered to this doctor's own department.
// That's what drives `relationship` below.
//
// `chartGated` (the old Lock-1 chart-read-gate's per-row re-check) is dropped,
// not ported: the redesign's replacing invariant is "authorized reads only,
// no app-side re-check" (AGENTS.md §2) — whatever this search returns IS
// authorized, so there's nothing left to gate here.
import { consultUrl } from '../bridge-core.js';

const OPD_STATE = 'https://healther.io/opd/state';
const MRN_SYS = 'https://healther.io/opd/mrn';

const nameOf = (p) => { if (!p || !p.name || !p.name[0]) return 'Patient'; const n = p.name[0]; return n.text || [(n.given || []).join(' '), n.family].filter(Boolean).join(' ') || 'Patient'; };
const ageOf = (bd) => { if (!bd) return null; const y = new Date().getUTCFullYear() - Number(String(bd).slice(0, 4)); return isNaN(y) ? null : y; };
const initials = (name) => String(name || '').trim().split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
const ext = (res, url) => ((res && res.extension) || []).find((e) => e.url === url);
const fmtDate = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }); } catch { return ''; } };

// Groups a Patient?_revinclude=Encounter:subject bundle into { patients,
// encountersByPatient } — patients keyed by 'Patient/<id>', encounters
// grouped under the same key and time-sorted newest-first.
export function groupPatientBundle(bundle) {
  const patients = []; const encountersByPatient = {};
  for (const entry of (bundle && bundle.entry) || []) {
    const r = entry.resource; if (!r) continue;
    if (r.resourceType === 'Patient') patients.push(r);
    else if (r.resourceType === 'Encounter') {
      const ref = r.subject && r.subject.reference;
      if (ref) (encountersByPatient[ref] = encountersByPatient[ref] || []).push(r);
    }
  }
  for (const encs of Object.values(encountersByPatient)) {
    encs.sort((a, b) => String((b.period && b.period.start) || '').localeCompare(String((a.period && a.period.start) || '')));
  }
  return { patients, encountersByPatient };
}

export function shapePatientRow(patient, encounters) {
  const name = nameOf(patient);
  const mrn = (patient.identifier || []).find((i) => i.system === MRN_SYS);
  const phone = (patient.telecom || []).find((t) => t.system === 'phone');
  const last = (encounters || [])[0] || null;
  const lastStateExt = last && ext(last, OPD_STATE);
  const lastState = lastStateExt ? lastStateExt.valueString : null;
  return {
    id: patient.id,
    medplumId: patient.id,
    displayName: name,
    initials: initials(name),
    age: ageOf(patient.birthDate),
    sex: patient.gender ? patient.gender[0].toUpperCase() : '',
    phone: phone ? phone.value : '',
    language: null,
    mrn: mrn ? mrn.value : '',
    encounterCount: (encounters || []).length,
    lastEncounterId: last ? last.id : null,
    lastEncounterState: lastState,
    // The FHIR extension already stores the view-level state string (see
    // shapeQueueRow) — no separate internal->view mapping needed client-side.
    lastEncounterStateView: lastState,
    lastEncounterAt: last && last.period ? last.period.start : null,
    lastEncounterAtLabel: last && last.period ? fmtDate(last.period.start) : '',
    lastEncounterDoctor: last && last.participant && last.participant[0] && last.participant[0].individual ? last.participant[0].individual.display || '' : '',
    lastEncounterDepartment: last && last.serviceProvider ? last.serviceProvider.display || '' : '',
    consultHref: last ? consultUrl(last.id, patient.id) : null,
    // Every readable Encounter here is already this doctor's own department
    // (RLS-scoped) — any encounter at all means 'department' relationship.
    relationship: (encounters || []).length > 0 ? 'department' : 'none',
    accessDetail: null,
    chartGated: false,
  };
}
